import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Mic2 } from "lucide-react";

const STEPS = ["Informasi Rapat", "Metadata & Tag", "Peserta"];

export default function MeetingCreate() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    title: "", agenda: "", location: "", division: "", organization: "", tags: "",
    scheduled_at: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
    duration_minutes: 60, language: "id", participant_ids: [], status: "scheduled",
  });

  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (id) => setF((p) => ({
    ...p, participant_ids: p.participant_ids.includes(id) ? p.participant_ids.filter((x) => x !== id) : [...p.participant_ids, id],
  }));

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Judul rapat wajib diisi"); setStep(0); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/meetings", {
        ...f,
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        scheduled_at: new Date(f.scheduled_at).toISOString(),
        duration_minutes: Number(f.duration_minutes) || 60,
      });
      toast.success("Rapat berhasil dibuat");
      nav(`/meetings/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl space-y-7" data-testid="meeting-create-page">
      <div>
        <Button variant="ghost" size="sm" className="mb-3 -ml-2 text-slate-500" onClick={() => nav("/meetings")} data-testid="back-btn">
          <ArrowLeft size={15} className="mr-1.5" /> Kembali
        </Button>
        <p className="label-xs">Rapat Baru</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Create Meeting</h1>
      </div>

      <div className="flex items-center gap-3" data-testid="stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <button onClick={() => setStep(i)} className="flex items-center gap-2.5 group" data-testid={`step-${i}`}>
              <span className={`grid place-items-center h-8 w-8 rounded-full text-xs font-bold transition-colors ${
                i < step ? "bg-emerald-500 text-white" : i === step ? "grad-brand text-white" : "bg-slate-100 text-slate-400"}`}>
                {i < step ? <Check size={14} /> : i + 1}
              </span>
              <span className={`hidden sm:block text-sm font-medium ${i === step ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
            </button>
            {i < STEPS.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="clay p-7 sm:p-8">
        {step === 0 && (
          <div className="space-y-6 animate-fade-up">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Judul Rapat *</Label>
              <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="h-11 rounded-xl"
                placeholder="Rapat Koordinasi Anggaran Kuartal II" data-testid="meeting-title-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Agenda</Label>
              <Textarea value={f.agenda} onChange={(e) => set("agenda", e.target.value)} rows={4} className="rounded-xl"
                placeholder="1. Pembukaan&#10;2. Pemaparan realisasi&#10;3. Diskusi&#10;4. Kesimpulan" data-testid="meeting-agenda-input" />
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Jadwal</Label>
                <Input type="datetime-local" value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} className="h-11 rounded-xl" data-testid="meeting-schedule-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Durasi (menit)</Label>
                <Input type="number" min={5} value={f.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="h-11 rounded-xl" data-testid="meeting-duration-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Bahasa</Label>
                <Select value={f.language} onValueChange={(v) => set("language", v)}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="meeting-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="id">Bahasa Indonesia</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ja">日本語</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6 animate-fade-up">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Lokasi</Label>
                <Input value={f.location} onChange={(e) => set("location", e.target.value)} className="h-11 rounded-xl" placeholder="Ruang Rapat Utama" data-testid="meeting-location-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Divisi</Label>
                <Input value={f.division} onChange={(e) => set("division", e.target.value)} className="h-11 rounded-xl" placeholder="Keuangan" data-testid="meeting-division-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Organisasi</Label>
                <Input value={f.organization} onChange={(e) => set("organization", e.target.value)} className="h-11 rounded-xl" placeholder="Kementerian Contoh" data-testid="meeting-organization-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-700">Tags (pisahkan koma)</Label>
                <Input value={f.tags} onChange={(e) => set("tags", e.target.value)} className="h-11 rounded-xl" placeholder="anggaran, kuartal-2" data-testid="meeting-tags-input" />
              </div>
            </div>
            <div className="space-y-2 max-w-xs">
              <Label className="text-xs font-semibold text-slate-700">Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="meeting-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Terjadwal</SelectItem>
                  <SelectItem value="completed">Selesai</SelectItem>
                  <SelectItem value="cancelled">Dibatalkan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 animate-fade-up">
            <div>
              <p className="text-sm font-semibold text-slate-800">Undang Peserta</p>
              <p className="mt-1 text-sm text-slate-500">Peserta dengan voiceprint terdaftar akan dikenali otomatis oleh mesin biometrik suara.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 max-h-[360px] overflow-y-auto pr-1" data-testid="participant-list">
              {users.map((u) => {
                const on = f.participant_ids.includes(u.id);
                return (
                  <label key={u.id} htmlFor={`p-${u.id}`}
                    className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${on ? "border-brand-600 bg-brand-50/60" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid={`participant-option-${u.id}`}>
                    <Checkbox id={`p-${u.id}`} checked={on} onCheckedChange={() => toggle(u.id)} />
                    <span className="grid place-items-center h-9 w-9 shrink-0 rounded-full grad-brand text-white text-[11px] font-semibold">
                      {u.name.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800">{u.name}</span>
                      <span className="block truncate text-xs text-slate-500">{u.role_label} · {u.division || "—"}</span>
                    </span>
                    {u.voice_enrolled && <Mic2 size={15} className="shrink-0 text-emerald-500" title="Voiceprint terdaftar" />}
                  </label>
                );
              })}
            </div>
            <p className="text-sm text-slate-500">{f.participant_ids.length} peserta dipilih.</p>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} className="text-slate-600" data-testid="prev-step-btn">
            <ArrowLeft size={15} className="mr-1.5" /> Sebelumnya
          </Button>
          {step < 2 ? (
            <Button className="h-11 rounded-xl grad-brand text-white px-6" onClick={() => setStep(step + 1)} data-testid="next-step-btn">
              Lanjut <ArrowRight size={15} className="ml-1.5" />
            </Button>
          ) : (
            <Button className="h-11 rounded-xl grad-brand text-white px-6" onClick={submit} disabled={busy} data-testid="submit-meeting-btn">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} Simpan Rapat
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
